import React, { Component } from "react";
import Table from "../../lib/tables";
import ContextMenu from "../../lib/context_menu";
import flatMap from "lodash/flatMap";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import map from "lodash/map";
import sortBy from "lodash/sortBy";
import uniq from "lodash/uniq";
import zipObject from "lodash/zipObject";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import {
  EpisodesByShowActionManager,
  ShowsForUserActionManager,
} from "../../../action_managers/shows";
import { getAudioBlocksForUser } from "../../../action_managers/audio_management";
import SolidBlueCircle from "./markers_table_solid_blue_circle";
import Spinner from "../../lib/spinner";

const additionalStyle = () => ({
  style: { overflow: "visible" },
});
const tBodyAdditionalStyle = () => ({ style: { overflow: "visible", minHeight: "64px" } });
const tdAdditionalStyle = () => ({
  style: {
    height: "80px",
    overflow: "visible",
    padding: "0",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
});

class MarkersTable extends Component {
  state = {
    isLoading: false,
  };

  componentDidMount() {
    if (this.props.user && isEmpty(this.props.episodes)) {
      this.setState({ isLoading: true }, () => {
        this.props.getShowsForUser(this.props.user).then(response => {
          if (response.status === 200) {
            const shows = response.json;
            let showEpisodesFetches = [];
            shows.forEach(show => {
              showEpisodesFetches.push(this.props.getEpisodesByShow(show.uuid));
            });
            Promise.all(showEpisodesFetches).then(() => {
              this.setState({ isLoading: false });
            });
          }
        });
        this.props.getAudioBlocksForUser(this.props.user);
      });
    }
  }

  shouldComponentUpdate(nextProps, nextState, nextContext) {
    const currentEpisodes = this.getFilteredEpisodes(this.props);
    const nextEpisodes = this.getFilteredEpisodes(nextProps);
    // necessary so we don't re-render and prevent the loader from showing when save button is clicked
    // otherwise instantly saves
    return (
      !isEqual(currentEpisodes, nextEpisodes) ||
      !isEqual(this.props.markers, nextProps.markers) ||
      this.state.isLoading !== nextState.isLoading
    );
  }

  getMarkersEpisodeUUIDs = markers => {
    return uniq(markers.map(marker => marker.episodeUUID));
  };

  getFilteredEpisodes = ({ markers = [], episodes = [] }) => {
    const episodeUUIDs = this.getMarkersEpisodeUUIDs(markers);
    return episodes.filter(episode => episodeUUIDs.includes(episode.uuid));
  };

  columns = (markersByEpisodes, audioBlockByUUID) => [
    {
      Header: props => (
        <div className="flex-row-container">
          <div style={{ flex: "3 0 0" }}>Episode</div>
          <div style={{ flex: "1 0 0" }}>Insertion Points</div>
        </div>
      ),
      accessor: "title",
      Cell: data => {
        const markers = markersByEpisodes[data.original.uuid];
        return (
          <div className="flex-row-container align-center">
            <div style={{ flex: "3 0 0", overflow: "hidden", textOverflow: "ellipsis" }}>
              <img
                width={48}
                height={48}
                src={`${data.original.imageURL ||
                  this.props.shows[data.original.showUUID].imageURL}?d=100x100`}
                alt="album-art"
                className="margin-right--medium"
              />
              <span className="bold ellipsis">{data.value}</span>
            </div>
            <div style={{ flex: "1 0 0" }} className="flex-row-container align-center">
              <div style={{ display: "flex", flexWrap: "wrap" }}>
                {this.renderBlueCircles(markers, audioBlockByUUID)}
              </div>
              <div className="flex-1" />
              <ContextMenu
                noCircle={true}
                menuItems={{
                  Edit: () => {
                    this.props.history.push(`/insertion-points/episodes/${data.original.uuid}`);
                  },
                }}
              />
            </div>
          </div>
        );
      },
    },
  ];

  renderBlueCircles = (markers, audioBlockByUUID) => {
    return sortBy(markers, "offsetMilliSeconds").map(marker =>
      this.renderBlueCircle(marker, audioBlockByUUID)
    );
  };

  renderBlueCircle = (marker, audioBlockByUUID) => {
    const audioBlock = get(audioBlockByUUID, marker.audioBlockUUID);
    return (
      <div key={marker.uuid}>
        <SolidBlueCircle
          marker={marker}
          selectedAudioBlock={audioBlock}
          audioBlockByUUID={audioBlockByUUID}
        />
      </div>
    );
  };

  render() {
    const { markers = [], showHeader = false } = this.props;
    if (this.state.isLoading) {
      return (
        <div className="audio-management-table__loader">
          <Spinner isLoading={this.state.isLoading} />
        </div>
      );
    }

    if (!markers.length || !this.props.episodes.length) {
      return (
        <div className="audio-management-table__empty-text">
          <p>There are no existing insertion points.</p>
        </div>
      );
    }

    const episodeUUIDs = this.getMarkersEpisodeUUIDs(markers);
    const markersByEpisodeUUID = zipObject(
      episodeUUIDs,
      episodeUUIDs.map(episodeUUID => markers.filter(marker => marker.episodeUUID === episodeUUID))
    );
    const filteredEpisodes = this.getFilteredEpisodes(this.props);

    const theadThStyle = showHeader ? { padding: "0", marginTop: "10px" } : { display: "none" };
    const theadStyle = showHeader ? { boxShadow: "none" } : { display: "none" };
    return (
      <Table
        sortable={false}
        resizable={false}
        className="markers-table"
        getTdProps={tdAdditionalStyle}
        getTbodyProps={tBodyAdditionalStyle}
        getTableProps={additionalStyle}
        getTheadThProps={() => ({
          style: theadThStyle,
        })}
        getTheadProps={() => ({
          style: theadStyle,
        })}
        noBottomLine
        data={filteredEpisodes}
        columns={this.columns(markersByEpisodeUUID, this.props.audioBlockByUUID)}
      />
    );
  }
}

export default connect(
  state => {
    const episodes = flatMap(state.episodesByShow, obj =>
      map(obj.episodes, episode => ({
        ...episode,
        imageURL: episode.imageURL || get(state, ["shows", "shows", episode.showUUID, "imageURL"]),
      }))
    );

    return {
      shows: state.shows.shows,
      user: state.user.user,
      episodes: episodes,
      audioBlockByUUID: state.audioBlocks.audioBlocks,
    };
  },
  {
    getShowsForUser: () => new ShowsForUserActionManager().run(),
    getEpisodesByShow: showUUID => new EpisodesByShowActionManager({ showUUID }).run(),
    getAudioBlocksForUser,
  }
)(withRouter(MarkersTable));
